import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { buildCsv } from '@/lib/csv';
import { downloadText } from '@/lib/download';
import {
    AlertCircle,
    ArrowUpDown,
    BarChart3,
    CheckCircle,
    CreditCard,
    DollarSign,
    Download,
    Eye,
    FileText,
    Plus,
    Receipt,
    Search,
    SortAsc,
    SortDesc,
    X
} from 'lucide-react';
import { useMemo, useState } from 'react';

interface PaymentRecord {
  _id?: string;
  invoiceId: string;
  invoiceNumber: string;
  paymentDate: Date;
  amount: number;
  paymentMethod: 'CASH' | 'CHEQUE' | 'ONLINE' | 'BANK_TRANSFER' | 'UPI';
  referenceNumber?: string;
  notes?: string;
  createdBy: string;
  createdAt: Date;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceType: 'SALES' | 'PURCHASE';
  customerName?: string;
  vendorName?: string;
  invoiceDate: Date;
  dueDate: Date;
  totalAmount: number;
  paidAmount: number;
  pendingAmount: number;
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
  currency: string;
}

const MOCK_INVOICES: Invoice[] = [
  {
    _id: '1',
    invoiceNumber: 'SI-001234',
    invoiceType: 'SALES',
    customerName: 'TechCorp India Private Limited',
    invoiceDate: new Date('2025-01-15'),
    dueDate: new Date('2025-02-14'),
    totalAmount: 15000,
    paidAmount: 10000,
    pendingAmount: 5000,
    status: 'SENT',
    currency: 'INR'
  },
  {
    _id: '2',
    invoiceNumber: 'SI-001235',
    invoiceType: 'SALES',
    customerName: 'Mumbai Fashion House',
    invoiceDate: new Date('2025-01-20'),
    dueDate: new Date('2025-02-19'),
    totalAmount: 25000,
    paidAmount: 25000,
    pendingAmount: 0,
    status: 'PAID',
    currency: 'INR'
  },
  {
    _id: '3',
    invoiceNumber: 'PI-001001',
    invoiceType: 'PURCHASE',
    vendorName: 'Global Logistics Partner',
    invoiceDate: new Date('2025-01-10'),
    dueDate: new Date('2025-01-25'),
    totalAmount: 8000,
    paidAmount: 0,
    pendingAmount: 8000,
    status: 'OVERDUE',
    currency: 'INR'
  },
  {
    _id: '4',
    invoiceNumber: 'SI-001236',
    invoiceType: 'SALES',
    customerName: 'Express Solutions Ltd',
    invoiceDate: new Date('2025-01-25'),
    dueDate: new Date('2025-02-24'),
    totalAmount: 18500,
    paidAmount: 5000,
    pendingAmount: 13500,
    status: 'SENT',
    currency: 'INR'
  }
];

const MOCK_PAYMENTS: PaymentRecord[] = [
  {
    _id: '1',
    invoiceId: '1',
    invoiceNumber: 'SI-001234',
    paymentDate: new Date('2025-01-25'),
    amount: 10000,
    paymentMethod: 'BANK_TRANSFER',
    referenceNumber: 'TXN123456',
    notes: 'Partial payment received',
    createdBy: 'user1',
    createdAt: new Date('2025-01-25')
  },
  {
    _id: '2',
    invoiceId: '2',
    invoiceNumber: 'SI-001235',
    paymentDate: new Date('2025-02-01'),
    amount: 25000,
    paymentMethod: 'UPI',
    referenceNumber: 'UPI789012',
    notes: 'Full payment received',
    createdBy: 'user1',
    createdAt: new Date('2025-02-01')
  }
];

const INVOICE_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled'
};

const INVOICE_TYPE_LABELS: Record<string, string> = {
  SALES: 'Sales',
  PURCHASE: 'Purchase'
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PAID: 'Fully Paid',
  PARTIAL: 'Partially Paid',
  PENDING: 'Unpaid'
};

const formatCurrency = (value: number) => `₹${value.toLocaleString()}`;
const formatDate = (value: Date) => new Date(value).toLocaleDateString();
const parseDateFilter = (value: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const parseNumberFilter = (value: string) => {
  if (!value) return null;
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const PaymentTracking = () => {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>(() => [...MOCK_INVOICES]);
  const [payments, setPayments] = useState<PaymentRecord[]>(() => [...MOCK_PAYMENTS]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  
  // Enhanced search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [quickFilters, setQuickFilters] = useState({
    status: 'all',
    invoiceType: 'all',
    paymentStatus: 'all'
  });
  const [dateFilters, setDateFilters] = useState({
    startDate: '',
    endDate: '',
    dueDateStart: '',
    dueDateEnd: ''
  });
  const [amountFilters, setAmountFilters] = useState({
    minAmount: '',
    maxAmount: '',
    minPending: '',
    maxPending: ''
  });
  const [sortConfig, setSortConfig] = useState({
    field: 'invoiceDate',
    direction: 'desc' as 'asc' | 'desc'
  });
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: '',
    paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '',
    notes: ''
  });

  const normalizedFilters = useMemo(() => {
    return {
      normalizedSearch: searchTerm.trim().toLowerCase(),
      invoiceStart: parseDateFilter(dateFilters.startDate),
      invoiceEnd: parseDateFilter(dateFilters.endDate),
      dueStart: parseDateFilter(dateFilters.dueDateStart),
      dueEnd: parseDateFilter(dateFilters.dueDateEnd),
      minAmount: parseNumberFilter(amountFilters.minAmount),
      maxAmount: parseNumberFilter(amountFilters.maxAmount),
      minPending: parseNumberFilter(amountFilters.minPending),
      maxPending: parseNumberFilter(amountFilters.maxPending)
    };
  }, [amountFilters.maxAmount, amountFilters.maxPending, amountFilters.minAmount, amountFilters.minPending, dateFilters.dueDateEnd, dateFilters.dueDateStart, dateFilters.endDate, dateFilters.startDate, searchTerm]);

  // Enhanced filtering logic
  const filteredInvoices = useMemo(() => {
    let filtered = [...invoices];
    const {
      normalizedSearch,
      invoiceStart,
      invoiceEnd,
      dueStart,
      dueEnd,
      minAmount,
      maxAmount,
      minPending,
      maxPending
    } = normalizedFilters;

    // Search filter
    if (normalizedSearch) {
      filtered = filtered.filter(invoice => 
        invoice.invoiceNumber.toLowerCase().includes(normalizedSearch) ||
        invoice.customerName?.toLowerCase().includes(normalizedSearch) ||
        invoice.vendorName?.toLowerCase().includes(normalizedSearch)
      );
    }

    // Quick filters
    if (quickFilters.status !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === quickFilters.status);
    }
    if (quickFilters.invoiceType !== 'all') {
      filtered = filtered.filter(invoice => invoice.invoiceType === quickFilters.invoiceType);
    }
    if (quickFilters.paymentStatus !== 'all') {
      if (quickFilters.paymentStatus === 'PAID') {
        filtered = filtered.filter(invoice => invoice.pendingAmount === 0);
      } else if (quickFilters.paymentStatus === 'PENDING') {
        filtered = filtered.filter(invoice => invoice.pendingAmount > 0);
      } else if (quickFilters.paymentStatus === 'PARTIAL') {
        filtered = filtered.filter(invoice => invoice.paidAmount > 0 && invoice.pendingAmount > 0);
      }
    }

    // Date filters
    if (invoiceStart) {
      filtered = filtered.filter(invoice => new Date(invoice.invoiceDate) >= invoiceStart);
    }
    if (invoiceEnd) {
      filtered = filtered.filter(invoice => new Date(invoice.invoiceDate) <= invoiceEnd);
    }
    if (dueStart) {
      filtered = filtered.filter(invoice => new Date(invoice.dueDate) >= dueStart);
    }
    if (dueEnd) {
      filtered = filtered.filter(invoice => new Date(invoice.dueDate) <= dueEnd);
    }

    // Amount filters
    if (minAmount !== null) {
      filtered = filtered.filter(invoice => invoice.totalAmount >= minAmount);
    }
    if (maxAmount !== null) {
      filtered = filtered.filter(invoice => invoice.totalAmount <= maxAmount);
    }
    if (minPending !== null) {
      filtered = filtered.filter(invoice => invoice.pendingAmount >= minPending);
    }
    if (maxPending !== null) {
      filtered = filtered.filter(invoice => invoice.pendingAmount <= maxPending);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.field) {
        case 'invoiceNumber':
          aValue = a.invoiceNumber;
          bValue = b.invoiceNumber;
          break;
        case 'totalAmount':
          aValue = a.totalAmount;
          bValue = b.totalAmount;
          break;
        case 'pendingAmount':
          aValue = a.pendingAmount;
          bValue = b.pendingAmount;
          break;
        case 'dueDate':
          aValue = a.dueDate;
          bValue = b.dueDate;
          break;
        default:
          aValue = a.invoiceDate;
          bValue = b.invoiceDate;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [invoices, normalizedFilters, quickFilters, sortConfig]);

  // Summary calculations
  const summaryStats = useMemo(() => {
    const totals = filteredInvoices.reduce(
      (acc, inv) => {
        acc.totalAmount += inv.totalAmount;
        acc.paidAmount += inv.paidAmount;
        acc.pendingAmount += inv.pendingAmount;
        if (inv.status === 'OVERDUE') {
          acc.overdueAmount += inv.pendingAmount;
        }
        return acc;
      },
      { totalAmount: 0, paidAmount: 0, pendingAmount: 0, overdueAmount: 0 }
    );
    const collectionRate =
      totals.totalAmount > 0
        ? (totals.paidAmount / totals.totalAmount) * 100
        : 0;
    return {
      totalInvoices: filteredInvoices.length,
      totalAmount: totals.totalAmount,
      paidAmount: totals.paidAmount,
      pendingAmount: totals.pendingAmount,
      overdueAmount: totals.overdueAmount,
      collectionRate
    };
  }, [filteredInvoices]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; value: string; onClear: () => void }> = [];
    if (normalizedFilters.normalizedSearch) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: searchTerm.trim(),
        onClear: () => setSearchTerm('')
      });
    }
    if (quickFilters.status !== 'all') {
      chips.push({
        id: 'status',
        label: 'Status',
        value: INVOICE_STATUS_LABELS[quickFilters.status] || quickFilters.status,
        onClear: () => setQuickFilters(prev => ({ ...prev, status: 'all' }))
      });
    }
    if (quickFilters.invoiceType !== 'all') {
      chips.push({
        id: 'invoice-type',
        label: 'Invoice Type',
        value: INVOICE_TYPE_LABELS[quickFilters.invoiceType] || quickFilters.invoiceType,
        onClear: () => setQuickFilters(prev => ({ ...prev, invoiceType: 'all' }))
      });
    }
    if (quickFilters.paymentStatus !== 'all') {
      chips.push({
        id: 'payment-status',
        label: 'Payment Status',
        value: PAYMENT_STATUS_LABELS[quickFilters.paymentStatus] || quickFilters.paymentStatus,
        onClear: () => setQuickFilters(prev => ({ ...prev, paymentStatus: 'all' }))
      });
    }
    if (normalizedFilters.invoiceStart) {
      chips.push({
        id: 'invoice-start',
        label: 'Invoice From',
        value: formatDate(normalizedFilters.invoiceStart),
        onClear: () => setDateFilters(prev => ({ ...prev, startDate: '' }))
      });
    }
    if (normalizedFilters.invoiceEnd) {
      chips.push({
        id: 'invoice-end',
        label: 'Invoice To',
        value: formatDate(normalizedFilters.invoiceEnd),
        onClear: () => setDateFilters(prev => ({ ...prev, endDate: '' }))
      });
    }
    if (normalizedFilters.dueStart) {
      chips.push({
        id: 'due-start',
        label: 'Due From',
        value: formatDate(normalizedFilters.dueStart),
        onClear: () => setDateFilters(prev => ({ ...prev, dueDateStart: '' }))
      });
    }
    if (normalizedFilters.dueEnd) {
      chips.push({
        id: 'due-end',
        label: 'Due To',
        value: formatDate(normalizedFilters.dueEnd),
        onClear: () => setDateFilters(prev => ({ ...prev, dueDateEnd: '' }))
      });
    }
    if (normalizedFilters.minAmount !== null) {
      chips.push({
        id: 'min-amount',
        label: 'Min Total',
        value: formatCurrency(normalizedFilters.minAmount),
        onClear: () => setAmountFilters(prev => ({ ...prev, minAmount: '' }))
      });
    }
    if (normalizedFilters.maxAmount !== null) {
      chips.push({
        id: 'max-amount',
        label: 'Max Total',
        value: formatCurrency(normalizedFilters.maxAmount),
        onClear: () => setAmountFilters(prev => ({ ...prev, maxAmount: '' }))
      });
    }
    if (normalizedFilters.minPending !== null) {
      chips.push({
        id: 'min-pending',
        label: 'Min Pending',
        value: formatCurrency(normalizedFilters.minPending),
        onClear: () => setAmountFilters(prev => ({ ...prev, minPending: '' }))
      });
    }
    if (normalizedFilters.maxPending !== null) {
      chips.push({
        id: 'max-pending',
        label: 'Max Pending',
        value: formatCurrency(normalizedFilters.maxPending),
        onClear: () => setAmountFilters(prev => ({ ...prev, maxPending: '' }))
      });
    }
    return chips;
  }, [normalizedFilters, quickFilters.invoiceType, quickFilters.paymentStatus, quickFilters.status, searchTerm]);

  const activeFilterCount = activeFilterChips.length;

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortConfig.direction === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />;
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'PAID': return 'default';
      case 'SENT': return 'secondary';
      case 'OVERDUE': return 'destructive';
      case 'DRAFT': return 'outline';
      case 'CANCELLED': return 'secondary';
      default: return 'outline';
    }
  };

  const getPaymentStatusLabel = (invoice: Invoice) => {
    if (invoice.pendingAmount === 0) {
      return 'Fully Paid';
    }
    if (invoice.paidAmount > 0) {
      return 'Partially Paid';
    }
    return 'Unpaid';
  };

  const getPaymentStatusBadge = (invoice: Invoice) => {
    const label = getPaymentStatusLabel(invoice);
    if (label === 'Fully Paid') {
      return <Badge variant="default" className="bg-green-100 text-green-800">Fully Paid</Badge>;
    }
    if (label === 'Partially Paid') {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Partially Paid</Badge>;
    }
    return <Badge variant="outline" className="bg-red-100 text-red-800">Unpaid</Badge>;
  };

  const recordPayment = async () => {
    try {
      if (!selectedInvoice) return;

      const amount = parseFloat(paymentForm.amount);
      if (amount <= 0 || amount > selectedInvoice.pendingAmount) {
        toast({
          title: 'Invalid Amount',
          description: `Amount must be between ₹1 and ${formatCurrency(selectedInvoice.pendingAmount)}`,
          variant: 'destructive'
        });
        return;
      }

      setIsLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      const newPayment: PaymentRecord = {
        _id: Date.now().toString(),
        invoiceId: selectedInvoice._id,
        invoiceNumber: selectedInvoice.invoiceNumber,
        paymentDate: new Date(paymentForm.paymentDate),
        amount,
        paymentMethod: paymentForm.paymentMethod as any,
        referenceNumber: paymentForm.referenceNumber,
        notes: paymentForm.notes,
        createdBy: 'current-user',
        createdAt: new Date()
      };

      setPayments(prev => [...prev, newPayment]);

      // Update invoice
      setInvoices(prev => prev.map(inv => 
        inv._id === selectedInvoice._id 
          ? { 
              ...inv, 
              paidAmount: inv.paidAmount + amount,
              pendingAmount: inv.pendingAmount - amount,
              status: inv.pendingAmount - amount === 0 ? 'PAID' : inv.status
            }
          : inv
      ));

      setShowPaymentDialog(false);
      setSelectedInvoice(null);
      setPaymentForm({
        amount: '',
        paymentMethod: '',
        paymentDate: new Date().toISOString().split('T')[0],
        referenceNumber: '',
        notes: ''
      });

      toast({
        title: 'Payment Recorded Successfully!',
        description: `${formatCurrency(amount)} payment recorded for ${selectedInvoice.invoiceNumber}`,
      });
    } catch (error) {
      toast({
        title: 'Payment Recording Failed',
        description: 'Please try again later',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setQuickFilters({ status: 'all', invoiceType: 'all', paymentStatus: 'all' });
    setDateFilters({ startDate: '', endDate: '', dueDateStart: '', dueDateEnd: '' });
    setAmountFilters({ minAmount: '', maxAmount: '', minPending: '', maxPending: '' });
  };

  const exportData = (format: 'csv' | 'pdf') => {
    if (format === 'pdf') {
      toast({
        title: 'PDF Export Coming Soon',
        description: 'CSV export is available now. PDF export will be added in a future update.',
      });
      return;
    }

    if (filteredInvoices.length === 0) {
      toast({
        title: 'No Invoices to Export',
        description: 'Adjust your filters or search to include invoices before exporting.',
        variant: 'destructive'
      });
      return;
    }

    const header = [
      'Invoice Number',
      'Invoice Type',
      'Party Name',
      'Party Type',
      'Invoice Date',
      'Due Date',
      'Invoice Status',
      'Payment Status',
      'Total Amount',
      'Paid Amount',
      'Pending Amount',
      'Currency'
    ];

    const rows = filteredInvoices.map((invoice) => [
      invoice.invoiceNumber,
      invoice.invoiceType,
      invoice.customerName || invoice.vendorName || '',
      invoice.invoiceType === 'SALES' ? 'Customer' : 'Vendor',
      formatDate(invoice.invoiceDate),
      formatDate(invoice.dueDate),
      INVOICE_STATUS_LABELS[invoice.status] || invoice.status,
      getPaymentStatusLabel(invoice),
      invoice.totalAmount,
      invoice.paidAmount,
      invoice.pendingAmount,
      invoice.currency
    ]);

    const csvContent = buildCsv(header, rows, { includeBom: true, quoteAll: true });
    downloadText(
      csvContent,
      `payment-tracking-${new Date().toISOString().split('T')[0]}.csv`,
      'text/csv;charset=utf-8;'
    );

    toast({
      title: 'Export Complete',
      description: `Exported ${filteredInvoices.length} invoices to CSV.`,
    });
  };

  const now = Date.now();

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h3 className="text-2xl font-bold tracking-tight">Payment Tracking</h3>
          <p className="text-muted-foreground">
            Track payments, record transactions, and manage collections
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:w-auto">
          <Button
            variant="outline"
            onClick={() => exportData('csv')}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={() => exportData('pdf')}
            className="w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Enhanced Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.totalInvoices}</div>
            <p className="text-xs text-muted-foreground">
              In current view
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summaryStats.totalAmount)}</div>
            <p className="text-xs text-muted-foreground">
              Gross invoice value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Collected</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(summaryStats.paidAmount)}</div>
            <p className="text-xs text-muted-foreground">
              Payments received
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Outstanding</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatCurrency(summaryStats.pendingAmount)}</div>
            <p className="text-xs text-muted-foreground">
              Pending collection
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.collectionRate.toFixed(1)}%</div>
            <div className="mt-2">
              <Progress value={summaryStats.collectionRate} className="h-2" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search & Filter Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Primary Search */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Search Invoices</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Invoice number, customer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape' && searchTerm) {
                      event.preventDefault();
                      setSearchTerm('');
                    }
                  }}
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-3 h-4 w-4 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Invoice Status</Label>
              <Select value={quickFilters.status} onValueChange={(value) => setQuickFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="SENT">Sent</SelectItem>
                  <SelectItem value="PAID">Paid</SelectItem>
                  <SelectItem value="OVERDUE">Overdue</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Invoice Type</Label>
              <Select value={quickFilters.invoiceType} onValueChange={(value) => setQuickFilters(prev => ({ ...prev, invoiceType: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="SALES">Sales</SelectItem>
                  <SelectItem value="PURCHASE">Purchase</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Payment Status</Label>
              <Select value={quickFilters.paymentStatus} onValueChange={(value) => setQuickFilters(prev => ({ ...prev, paymentStatus: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payment Status</SelectItem>
                  <SelectItem value="PAID">Fully Paid</SelectItem>
                  <SelectItem value="PARTIAL">Partially Paid</SelectItem>
                  <SelectItem value="PENDING">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Advanced Filters */}
          <Tabs defaultValue="dates" className="w-full">
            <TabsList>
              <TabsTrigger value="dates">Date Filters</TabsTrigger>
              <TabsTrigger value="amounts">Amount Filters</TabsTrigger>
            </TabsList>

            <TabsContent value="dates" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Invoice From</Label>
                  <Input
                    type="date"
                    value={dateFilters.startDate}
                    onChange={(e) => setDateFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Invoice To</Label>
                  <Input
                    type="date"
                    value={dateFilters.endDate}
                    onChange={(e) => setDateFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due From</Label>
                  <Input
                    type="date"
                    value={dateFilters.dueDateStart}
                    onChange={(e) => setDateFilters(prev => ({ ...prev, dueDateStart: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due To</Label>
                  <Input
                    type="date"
                    value={dateFilters.dueDateEnd}
                    onChange={(e) => setDateFilters(prev => ({ ...prev, dueDateEnd: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="amounts" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Min Total (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={amountFilters.minAmount}
                    onChange={(e) => setAmountFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Total (₹)</Label>
                  <Input
                    type="number"
                    placeholder="No limit"
                    value={amountFilters.maxAmount}
                    onChange={(e) => setAmountFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min Pending (₹)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={amountFilters.minPending}
                    onChange={(e) => setAmountFilters(prev => ({ ...prev, minPending: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Pending (₹)</Label>
                  <Input
                    type="number"
                    placeholder="No limit"
                    value={amountFilters.maxPending}
                    onChange={(e) => setAmountFilters(prev => ({ ...prev, maxPending: e.target.value }))}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {activeFilterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Active filters
              </span>
              {activeFilterChips.map((chip) => (
                <Badge key={chip.id} variant="secondary" className="gap-1 pr-1 text-xs font-medium">
                  <span className="max-w-[220px] truncate" title={`${chip.label}: ${chip.value}`}>
                    {chip.label}: {chip.value}
                  </span>
                  <button
                    type="button"
                    onClick={chip.onClear}
                    className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/70"
                    aria-label={`Clear ${chip.label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {filteredInvoices.length} of {invoices.length} invoices
              {activeFilterCount > 0 && (
                <span className="ml-2">• {activeFilterCount} filters</span>
              )}
            </div>
            <Button
              variant="outline"
              onClick={clearAllFilters}
              size="sm"
              className="w-full sm:w-auto"
              disabled={activeFilterCount === 0}
            >
              <X className="h-4 w-4 mr-2" />
              Clear All Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Invoice Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Invoice & Payment Management
          </CardTitle>
          <CardDescription>
            Track invoice payments and record new transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Invoices Found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search criteria or filters
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="hidden md:block rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('invoiceNumber')}
                      >
                        <div className="flex items-center gap-2">
                          Invoice Number
                          {getSortIcon('invoiceNumber')}
                        </div>
                      </TableHead>
                      <TableHead>Customer/Vendor</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('totalAmount')}
                      >
                        <div className="flex items-center gap-2">
                          Total Amount
                          {getSortIcon('totalAmount')}
                        </div>
                      </TableHead>
                      <TableHead>Paid Amount</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('pendingAmount')}
                      >
                        <div className="flex items-center gap-2">
                          Pending
                          {getSortIcon('pendingAmount')}
                        </div>
                      </TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort('dueDate')}
                      >
                        <div className="flex items-center gap-2">
                          Due Date
                          {getSortIcon('dueDate')}
                        </div>
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => {
                      const isOverdue =
                        new Date(invoice.dueDate).getTime() < now &&
                        invoice.pendingAmount > 0;
                      return (
                        <TableRow key={invoice._id}>
                          <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {invoice.customerName || invoice.vendorName}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {invoice.invoiceType === 'SALES' ? 'Customer' : 'Vendor'}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={invoice.invoiceType === 'SALES' ? 'default' : 'secondary'}>
                              {invoice.invoiceType}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">{formatCurrency(invoice.totalAmount)}</TableCell>
                          <TableCell className="text-green-600">{formatCurrency(invoice.paidAmount)}</TableCell>
                          <TableCell className="text-orange-600">{formatCurrency(invoice.pendingAmount)}</TableCell>
                          <TableCell>{getPaymentStatusBadge(invoice)}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadgeVariant(invoice.status)}>
                              {invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className={isOverdue ? 'text-red-600 font-medium' : ''}>
                              {formatDate(invoice.dueDate)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setShowPaymentDialog(true);
                                }}
                                disabled={invoice.pendingAmount === 0}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-4 md:hidden">
                {filteredInvoices.map((invoice) => {
                  const isOverdue =
                    new Date(invoice.dueDate).getTime() < now &&
                    invoice.pendingAmount > 0;
                  const partyLabel = invoice.invoiceType === 'SALES' ? 'Customer' : 'Vendor';
                  return (
                    <div key={invoice._id} className="rounded-lg border p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-xs text-muted-foreground">{partyLabel}</div>
                          <div className="text-base font-semibold">
                            {invoice.customerName || invoice.vendorName}
                          </div>
                          <div className="text-sm text-muted-foreground">{invoice.invoiceNumber}</div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant={invoice.invoiceType === 'SALES' ? 'default' : 'secondary'}>
                            {invoice.invoiceType}
                          </Badge>
                          <Badge variant={getStatusBadgeVariant(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-muted-foreground">Total</div>
                          <div className="font-semibold">{formatCurrency(invoice.totalAmount)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Paid</div>
                          <div className="font-semibold text-green-600">{formatCurrency(invoice.paidAmount)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Pending</div>
                          <div className="font-semibold text-orange-600">{formatCurrency(invoice.pendingAmount)}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Due Date</div>
                          <div className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {formatDate(invoice.dueDate)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        {getPaymentStatusBadge(invoice)}
                        <span className="text-xs text-muted-foreground">
                          Invoice Date {formatDate(invoice.invoiceDate)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowPaymentDialog(true);
                          }}
                          disabled={invoice.pendingAmount === 0}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Record
                        </Button>
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Payment Recording Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              Record a new payment for {selectedInvoice?.invoiceNumber}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Summary */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Total Amount</div>
                    <div className="font-semibold">{formatCurrency(selectedInvoice.totalAmount)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Paid Amount</div>
                    <div className="font-semibold text-green-600">{formatCurrency(selectedInvoice.paidAmount)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Pending Amount</div>
                    <div className="font-semibold text-orange-600">{formatCurrency(selectedInvoice.pendingAmount)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Due Date</div>
                    <div className="font-semibold">{formatDate(selectedInvoice.dueDate)}</div>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Amount (₹)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                      max={selectedInvoice.pendingAmount}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select 
                    value={paymentForm.paymentMethod} 
                    onValueChange={(value) => setPaymentForm(prev => ({ ...prev, paymentMethod: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select payment method" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="CHEQUE">Cheque</SelectItem>
                      <SelectItem value="ONLINE">Online Transfer</SelectItem>
                      <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Reference Number</Label>
                  <Input
                    placeholder="Transaction ID, cheque number, etc."
                    value={paymentForm.referenceNumber}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    placeholder="Additional notes about this payment..."
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPaymentDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={recordPayment}
                  disabled={isLoading || !paymentForm.amount || !paymentForm.paymentMethod}
                >
                  {isLoading ? 'Recording...' : 'Record Payment'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentTracking;
